import settings from '../../settings.json';
import {
  accountTypes,
  rolloverTypes,
  I_CLASS_MINIMUM
} from '../../flows/shared/utils';
import { minimumSummitIClassInvestAmount } from '../../flows/shared/summit.utils';

export const clean = (value = '') => {
  return value.replace(/\s+|-|[.(),!@#$%^*]/gi, '');
};

export const defaultToggleButtons = (namesArray, chosen = '') =>
  namesArray.map(item => ({
    name: item,
    active: !!(
      ['Tax-Free Money Market', 'Taxable Money Market'].includes(item) &&
      chosen === 'viewAllMoneyMarketFunds'
    )
  }));

export const removeHiddenFunds = funds =>
  funds.filter(
    ele => ele.trpCode && !settings.fundsToHideTrpCode.includes(ele.trpCode)
  );

export const iClassFunds = (
  totalAmount,
  allMutualFunds,
  iClassFundsReward = false
) =>
  totalAmount &&
  (iClassFundsReward
    ? totalAmount >= minimumSummitIClassInvestAmount
    : totalAmount >= I_CLASS_MINIMUM)
    ? allMutualFunds
    : allMutualFunds.filter(({ iClass }) => !iClass);

export const handleArrayFilter = (
  filteredFunds,
  arrayOfFilters,
  propertyName
) => {
  if (arrayOfFilters.length === 0) return filteredFunds;
  return filteredFunds.filter(filterNode =>
    arrayOfFilters.includes(filterNode[propertyName])
  );
};

export const handleBooleanFilter = (
  filteredFunds,
  needToFilter,
  propertyName
) => {
  if (!needToFilter) return filteredFunds;
  return filteredFunds.filter(elementFund => elementFund[propertyName]);
};

export const handleFundsFilter = (
  filteredFunds,
  needToFilter,
  propertyName
) => {
  if (!needToFilter) return filteredFunds;
  return filteredFunds.filter(
    elementFund => elementFund[propertyName] === false
  );
};

export const handleStringFilter = (
  filteredFunds,
  filterText,
  propertyName1,
  propertyName2
) => {
  if (filterText.length === 0) return filteredFunds;
  return filteredFunds.filter(fund => {
    const value1 = fund[propertyName1];
    const value2 = fund[propertyName2];
    return filterText === `${value1} (${value2})`;
  });
};

export const filterFunds = (
  fundsToFilter = [],
  filterText = '',
  assetClassFilters = [],
  morningstarFilters = [],
  openToNewInvestorsFilter = false,
  selectFundsFilter = false,
  iClassFilter = false,
  closedFundsFilter = false,
  investorClassFilter = false
) => {
  let filteredFunds = [...fundsToFilter];

  filteredFunds = handleArrayFilter(
    filteredFunds,
    assetClassFilters,
    'assetClassName'
  );

  filteredFunds = handleArrayFilter(
    filteredFunds,
    morningstarFilters,
    'morningStarOverallRating'
  );

  filteredFunds = handleBooleanFilter(
    filteredFunds,
    openToNewInvestorsFilter,
    'openToNewInvestors'
  );

  filteredFunds = handleBooleanFilter(
    filteredFunds,
    closedFundsFilter,
    'isClosedFund'
  );

  filteredFunds = handleBooleanFilter(
    filteredFunds,
    selectFundsFilter,
    'selectFund'
  );

  filteredFunds = handleBooleanFilter(filteredFunds, iClassFilter, 'iClass');
  filteredFunds = handleFundsFilter(
    filteredFunds,
    investorClassFilter,
    'iClass'
  );

  filteredFunds = handleStringFilter(
    filteredFunds,
    filterText,
    'name',
    'ticker'
  );

  return filteredFunds;
};

export const groupOrder = [
  'Domestic Stock',
  'Domestic Taxable Bond',
  'International/Global Stock',
  'International/Global Bond',
  'Tax-Free Money Market',
  'Domestic Tax-Free Bond',
  'Blended Asset',
  'Taxable Money Market'
];

const assetClassNameCallback = funds =>
  funds.reduce((acc, fund) => {
    let { assetClassName } = fund;
    if (assetClassName === 'Multi-Asset') {
      assetClassName = 'Blended Asset';
    }
    if (assetClassName === 'Equity' || assetClassName === 'US Equity') {
      assetClassName = 'Domestic Stock';
    }
    if (!acc[assetClassName]) {
      acc[assetClassName] = [];
    }
    acc[assetClassName].push(fund);
    return acc;
  }, {});

export const groupFunds = funds => {
  const groupedFunds = assetClassNameCallback(funds);

  return groupOrder.reduce((accum, curr) => {
    const currentFunds = groupedFunds[curr] || [];

    if (currentFunds.length === 0) {
      return accum;
    }

    return { ...accum, [curr]: currentFunds };
  }, {});
};

export const groupFundsTotals = funds => {
  const groupedFunds = assetClassNameCallback(funds);

  return groupOrder.reduce((accum, curr) => {
    const currentFunds = groupedFunds[curr] || [];

    if (currentFunds.length === 0) {
      return accum;
    }

    return { ...accum, [curr]: currentFunds.length };
  }, {});
};

export const extractActiveFilters = (filters = []) =>
  filters.filter(({ active }) => active).map(({ name }) => name);

export const formatFunds = (
  allMutualFunds,
  accountType,
  rolloverType,
  totalAmount,
  rewardsAvailable = {}
) => {
  const modifiedMutualFunds = allMutualFunds.map(acc => {
    let { assetClassName } = acc;
    if (assetClassName === 'Multi-Asset') {
      assetClassName = 'Blended Asset';
    }
    if (assetClassName === 'Equity' || assetClassName === 'US Equity') {
      assetClassName = 'Domestic Stock';
    }
    acc.assetClassName = assetClassName;
    return acc;
  }, {});

  const {
    closedFunds: closedFundsReward,
    iClassFunds: iClassFundsReward
  } = rewardsAvailable;

  const rolloverTypeDirectOrUndefined = newFund =>
    !newFund.openToNewInvestors &&
    newFund.openToSubsequentInvestments &&
    accountType === accountTypes.ROLLOVER &&
    rolloverType !== rolloverTypes.INDIRECT;

  const rolloverTypeIndirect = newFund =>
    newFund.openToNewInvestors &&
    newFund.isClosedFund &&
    accountType === accountTypes.ROLLOVER &&
    rolloverType === rolloverTypes.INDIRECT;

  const hasClosedFundsReward = newFund =>
    !newFund.openToNewInvestors &&
    newFund.openToSubsequentInvestments &&
    closedFundsReward;

  const hasIClassFundsReward = newFund => newFund.iClass && iClassFundsReward;

  const closedFundRulesApplied = modifiedMutualFunds.map(fund => {
    const newFund = fund;

    if (!newFund.openToNewInvestors) newFund.isClosedFund = true;

    if (rolloverTypeDirectOrUndefined(newFund)) {
      newFund.openToNewInvestors = true;
      newFund.unlockedFund = true;
    }
    if (rolloverTypeIndirect(newFund)) {
      newFund.openToNewInvestors = false;
      newFund.unlockedFund = false;
    }
    if (hasClosedFundsReward(newFund)) {
      newFund.openToNewInvestors = true;
      newFund.unlockedFund = true;
    }

    return newFund;
  });

  const iClassFundsReturn = iClassFunds(
    totalAmount,
    closedFundRulesApplied,
    iClassFundsReward
  );
  const iClassRulesApplied = removeHiddenFunds(iClassFundsReturn);

  const summitIClassRulesApplied = iClassRulesApplied.map(fund => {
    const newFund = fund;

    if (hasIClassFundsReward(newFund)) {
      newFund.unlockedFund = true;
    }
    return newFund;
  });

  const finalFormattedFunds = summitIClassRulesApplied;

  return finalFormattedFunds;
};

export const getAssetClassNames = funds => [
  ...new Set(funds.map(({ assetClassName }) => assetClassName))
];
export const filterOutClosedFunds = MutualFunds =>
  MutualFunds.filter(({ openToNewInvestors }) => openToNewInvestors);
